import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { tap } from 'rxjs';

import { AuthActions } from '@shared/store/auth.actions';
import { EntityState } from '@shared/store/entity.state';
import { StateModel } from '@shared/store/state.type';
import { StoreActions } from '@shared/store/store.actions';

import { Notification } from '../models/notification.model';
import { NotificationsApiService } from '../services/notifications-api.service';
import { NotificationsActions } from './notifications.actions';

export type NotificationsStateModel = StateModel<Notification>;

export const NOTIFICATIONS_STATE_TOKEN =
    new StateToken<NotificationsStateModel>('NotificationsState');

const defaultState: NotificationsStateModel = {
  entities: {},
  selected: '',
  ids: [],
  isLoaded: false,
  hasError: false,
};

@State<NotificationsStateModel>({
  name: NOTIFICATIONS_STATE_TOKEN,
  defaults: defaultState,
})
@Injectable()
export class NotificationsState extends EntityState {

  private _notificationsService = inject(NotificationsApiService);

  @Selector()
  static unread(state: NotificationsStateModel): Notification[] {
    return EntityState.getEntitiesList(state)
                      .filter((notification) => !notification.read);
  }

  @Selector()
  static read(state: NotificationsStateModel): Notification[] {
    return EntityState.getEntitiesList(state)
                      .filter((notification) => notification.read);
  }

  @Action(NotificationsActions.Get)
  getAll(ctx: StateContext<NotificationsStateModel>) {
    return this._notificationsService
               .get$()
               .pipe(
                   tap((items) => {
                     if (!items) {
                       return;
                     }
                     ctx.patchState({
                       ...EntityState.overwriteState(ctx.getState(), items),
                       ...EntityState.getApiLoadSuccessConfig(),
                     });
                   }),
               );
  }

  @Action(NotificationsActions.GetRead)
  getRead(ctx: StateContext<NotificationsStateModel>) {
    return this._notificationsService
               .getRead$()
               .pipe(
                   tap((items) => {
                     if (!items) {
                       return;
                     }
                     ctx.patchState({
                       ...EntityState.getStateWithNewValues(
                           ctx.getState(),
                           items,
                       ),
                       ...EntityState.getApiLoadSuccessConfig(),
                     });
                   }),
               );
  }

  @Action(NotificationsActions.GetUnread)
  getUnread(ctx: StateContext<NotificationsStateModel>) {
    return this._notificationsService
               .getUnread$()
               .pipe(
                   tap((items) => {
                     if (!items) {
                       return;
                     }
                     ctx.patchState({
                       ...EntityState.getStateWithNewValues(
                           ctx.getState(),
                           items,
                       ),
                       ...EntityState.getApiLoadSuccessConfig(),
                     });
                   }),
               );
  }

  @Action(NotificationsActions.GetById)
  getById(
      ctx: StateContext<NotificationsStateModel>,
      action: NotificationsActions.GetById,
  ) {
    return this._notificationsService
               .getById$(action.payload)
               .pipe(
                   tap((item) => {
                     if (!item) {
                       return;
                     }
                     ctx.patchState({
                       ...EntityState.getStateWithNewValues(
                           ctx.getState(),
                           [item],
                       ),
                       ...EntityState.getApiLoadSuccessConfig(),
                     });
                   }),
               );
  }

  @Action(NotificationsActions.MarkRead)
  markRead(ctx: StateContext<NotificationsStateModel>) {

    const unread = NotificationsState.unread(ctx.getState());
    const uuid: string[] = unread.map((item) => item.id);

    return this._notificationsService
               .markRead$({ uuid })
               .pipe(
                   tap((items) => {
                     if (!items) {
                       return;
                     }
                     ctx.patchState({
                       ...EntityState.getUpdatedEntities(ctx.getState(), items),
                       ...EntityState.getApiLoadSuccessConfig(),
                     });
                   }),
               );
  }

  @Action(NotificationsActions.MarkUnread)
  markUnread(ctx: StateContext<NotificationsStateModel>) {
    return this._notificationsService
               .markUnread$({ uuid: [] })
               .pipe(
                   tap((items) => {
                     if (!items) {
                       return;
                     }
                     ctx.patchState({
                       ...EntityState.getUpdatedEntities(ctx.getState(), items),
                       ...EntityState.getApiLoadSuccessConfig(),
                     });
                   }),
               );
  }

  @Action([
    NotificationsActions.GetFailed,
    NotificationsActions.GetReadFailed,
    NotificationsActions.GetUnreadFailed,
    NotificationsActions.GetByIdFailed,
    NotificationsActions.MarkReadFailed,
    NotificationsActions.MarkUnreadFailed,
  ])
  requestFailed(ctx: StateContext<NotificationsStateModel>) {
    ctx.patchState(EntityState.getApiLoadErrorConfig());
  }

  @Action(NotificationsActions.Select)
  select(
      ctx: StateContext<NotificationsStateModel>,
      action: NotificationsActions.Select,
  ) {
    ctx.patchState({
      selected: action.payload,
    });
  }

  @Action([
    StoreActions.ResetLoadedState,
    NotificationsActions.ResetLoadedState,
  ])
  resetLoadedState(ctx: StateContext<NotificationsStateModel>) {
    ctx.patchState({
      isLoaded: defaultState.isLoaded,
      hasError: defaultState.hasError,
    });
  }

  @Action([
    NotificationsActions.ResetState,
    StoreActions.ResetStore,
    AuthActions.Unauthorized,
  ])
  resetState(ctx: StateContext<NotificationsStateModel>) {
    ctx.setState({
      ...defaultState,
    });
  }
}
